/* eslint-disable @typescript-eslint/camelcase */

import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useHandleExistingAccount from './handleExistingAccount';
import { Status } from 'hooks/requests/fetchUtils';
import useFetchApi from 'hooks/useFetchApi';
import useQueryParameters from 'hooks/useQueryParameters';
import {
  CreditApplication,
  mergeApplication,
  lockFields,
  selectCreditApplication,
} from 'state/creditApplication';
import { setAccounts, CreditAccount } from 'state/instantCredit';
import { selectTenant } from 'state/tenant';
import { keys, pick, pickBy, isNil, complement, isEmpty } from 'ramda';
import isPresent from 'utils/isPresent';
import { setPrescreenId } from 'state/prescreen';
import { mergePrequalOffer } from 'state/prequalOffer';
import useAnalyticsVariables from 'hooks/useAnalyticVariables';
import useApplicationBasedOverrides from 'hooks/useApplicationBasedOverrides';
import useFmcHost from 'hooks/useFmcHost';
import { receiveExistingAccounts } from 'fmc-sdk';
import { useMockAccountData } from 'hooks/useMockAccountData';

const PATH = '/api/multi_account_virtual_lookup';

export type VirtualLookupSearchParams =
  | {
      prescreenId: string;
    }
  | {
      prequalificationId: string;
    };

export interface SearchResults {
  hasExistingAccount: false;
  returnCode: string | undefined;
  errorMessage: string | undefined;
  errorCode: string | undefined;
  address1: string | undefined;
  address2: string | undefined;
  city: string | undefined;
  state: string | undefined;
  zip: string | undefined;
  ssn: string | undefined;
  firstName: string | undefined;
  middleInitial: string | undefined;
  lastName: string | undefined;
  isExpired: boolean | undefined;
  // Perhaps discriminated type is better here.
  prescreenId: string | undefined;
  prequalificationId: string | undefined;
  creditLimit: number | undefined;
  // The prescreen case appears to be able to return these elements
  // without an existing account
  applicationId: string | undefined;
  accountId: string | undefined;
}

export interface ExistingAccountResult {
  hasExistingAccount: true;
  existingAccounts: CreditAccount[];
}

export type VirtualLookupResult = SearchResults | ExistingAccountResult;

type VirtualLookupHook = () => [
  (params: VirtualLookupSearchParams) => Promise<VirtualLookupResult>,
  Status,
  boolean,
];

export class VirtualLookupError extends Error {}

export const prefillValues = (
  results: SearchResults,
): Partial<CreditApplication> =>
  pickBy(complement(isNil), {
    firstName: results.firstName,
    lastName: results.lastName,
    address1: results.address1,
    address2: results.address2,
    city: results.city,
    state: results.state,
    zip: results.zip,
    ssn: results.ssn,
  });

const useVirtualLookup: VirtualLookupHook = () => {
  const [error, setError] = useState<boolean>(false);
  const [status, fetch] = useFetchApi();
  const dispatch = useDispatch();
  const {
    mockVL,
    location,
    channel,
    subchannel,
    applicationSubType,
  } = useQueryParameters();
  const fmcDispatch = useFmcHost();
  const handleExistingAccount = useHandleExistingAccount();
  const creditApplication = useSelector(selectCreditApplication);
  const tenant = useSelector(selectTenant);
  const overrideAttributes = useApplicationBasedOverrides();
  const { setAnalyticsVariables } = useAnalyticsVariables();
  const mockAccountData = useMockAccountData();

  const lookupVirtual = useCallback(
    async (params: VirtualLookupSearchParams): Promise<VirtualLookupResult> => {
      setError(false);
      const sharedParams = {
        mockResponse: mockVL,
        ...mockAccountData,
        location,
        channel,
        subchannel,
        applicationSubType,
      };
      const idParams =
        'prescreenId' in params
          ? { prescreenId: params.prescreenId }
          : { prequalificationId: params.prequalificationId };

      const response = await fetch(PATH, {
        method: 'POST',
        body: {
          ...sharedParams,
          ...overrideAttributes,
          ...idParams,
        },
      });
      if (!response.ok) {
        setError(true);
        return Promise.reject(new VirtualLookupError());
      }

      let searchResults: VirtualLookupResult = await response.json();

      if (tenant.trustedBrandPrefill) {
        const searchResultsKeys = (keys(
          searchResults,
        ) as unknown) as (keyof SearchResults)[];

        searchResults = pick(searchResultsKeys, {
          ...searchResults,
          ...(pickBy(isPresent, creditApplication) as object),
        }) as SearchResults;
      }

      if (searchResults.hasExistingAccount) {
        const existingAccounts = searchResults.existingAccounts;
        if (!isEmpty(existingAccounts)) {
          dispatch(setAccounts(searchResults.existingAccounts));
          if (tenant.enabledFeatures.virtualLookupMultipleAccounts)
            fmcDispatch(
              receiveExistingAccounts(
                searchResults.existingAccounts.map(account => {
                  return {
                    message: 'Received existing account',
                    callId: account.callId || null,
                    rewards: account.rewards,
                    cardType: account.cardType,
                    ...account,
                  };
                }),
              ),
            );
        }
        if (searchResults.existingAccounts.length === 1) {
          handleExistingAccount(searchResults.existingAccounts[0], {
            sendToSdk: true,
          });
        }
      } else {
        setAnalyticsVariables({
          applicationId: searchResults.applicationId,
          accountId: searchResults.accountId,
        });

        dispatch(mergeApplication(prefillValues(searchResults)));

        if (searchResults.prescreenId) {
          dispatch(setPrescreenId(searchResults.prescreenId));
        } else if (searchResults.prequalificationId) {
          dispatch(
            lockFields({
              ssn: Boolean(searchResults.ssn),
            }),
          );
          dispatch(mergePrequalOffer(searchResults));
        }
      }

      return searchResults;
    },
    [
      mockVL,
      mockAccountData,
      location,
      channel,
      subchannel,
      applicationSubType,
      fetch,
      overrideAttributes,
      tenant.trustedBrandPrefill,
      tenant.enabledFeatures.virtualLookupMultipleAccounts,
      creditApplication,
      dispatch,
      fmcDispatch,
      handleExistingAccount,
      setAnalyticsVariables,
    ],
  );

  return [lookupVirtual, status, error];
};

export default useVirtualLookup;
